import React, {useMemo, useRef, useState} from "react";
import {PopupPanel} from "../tools/UIPopMenu";
import {useColorModeTheme} from "../tools/CommonReact";
import {checkRoleViewOnly} from "../app/CommonUIData";
import {dispUserName, Divider, DotIndicator, getUserIfExt, LetterAvatar, Title} from "../app/CommonUIComponenets";
import {appCtx, genTooltip, queryOrErr, useFetchData} from "../app/AppContext";
import {downloadFile, nameExport, socketNotifs} from "../app/Exports";
import {icn} from "../tools/UIIcons";
import {Button, TextInput, useInputChange} from "../tools/Inputs";
import {useSidePanel} from "../tools/UIPopSidePanel";
import {useFeatureFlags, useCheckRefresh, useUser} from "../app/UseAppData";
import {commafyPlural, objToArrK, pickKeys, getAgo} from "../tools/CommonJS";
import {classNameScrollbarThin} from "../tools/CommonHtml";
import {Switch} from "../tools/UIControls";
import {notifTypes} from "../routes";
import {checkRum} from "../app/rum";
import {mockData} from "../app/MockData.js";
import {SearchGlobalPanel} from "./GlobalSearch";
import {useCheckModuleRedirect} from "../app/UseModuleDisplay.js";
import {ROUTES} from "../routePaths.js";

const themeMenuDefs = {
        light: {h: "Light", i: icn.sun},
        dark: {h: "Dark", i: <i className="h-4 w-4 text-end leading-4 block icon-moon-o text-title"/>},
    // "": {h: "System", i: icn.sunCloud},
    }, themeMenuDefsOpts = objToArrK(themeMenuDefs);

const notifTitlesByUser = {
    me: {clientLog: {h: "You have requested logs"}, offlineExport: {h: "You have exported data"}},
    team: {clientLog: {h: " has requested logs"}, offlineExport: {h: " has exported data"}},
    system: {clientLog: {h: "Sensor logs for"}, offlineExport: {h: "Exported data for"}}
};

export function NavBar() {
    const vals = useRef({}).current;
    const {curTheme, setTheme} = useColorModeTheme();
    const {inp, onChange} = useInputChange({search: ""});
    const {setPanel, panelsJsx} = useSidePanel();
    const {user, signOut} = useUser();
    const {rsp: ftrFlags} = useFeatureFlags();
    useCheckModuleRedirect();
    checkRum({vals, ftrFlags});
    useCheckRefresh();

    const openSearch = () => vals.panelId = setPanel({
        Elm: SearchGlobalPanel, panelId: vals.panelId,
        className: "bg-bg2 w-[28rem] h-[100vh] top-0 right-0 p-4 rounded-l-xl shadow-lg",
        classNameOverlay: "bg-handle"
    });

    return (
        <div className="h-20 shrink-0 px-8 flex items-center gap-4 bg-bg2">
            <Breadcrumbs className="grow"/>
            {/*<RefreshButton obj={refreshData} onRefresh={refreshData?.onRefresh}/>*/}
            <div className="flex items-center gap-6">
                <TextInput placeholder="Global Search" onFocus={openSearch} field="global-search" className="min-w-12" value={inp.search} onChange={onChange}/>
                <Button
                    className="icon-activity cursor-pointer" {...genTooltip("My Activity")}
                    onClick={() => appCtx.nav.to(ROUTES.ACTION_LOG, {user: user?.user}, {isReset: true})} disabled={!user}/>
                <Notifications/>
                <PopupPanel
                    button={<Button>
                        <LetterAvatar user={user?.userName || user?.user} className="h-8 bg-[#5f5e90] ring-[#28248b] ring-2 border-2 border-bg2"/>
                    </Button>}
                    align="bottom-right"
                    onShowPanel={() =>
                        <div className="bg-popup border-prm-main border p-2 text-title rounded-lg whitespace-pre shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none flex flex-col gap-2">
                            <div className="flex gap-2 items-center">
                                <i className="icon-user text-xl border-2 border-tx-steel ring-1 ring-prm-main rounded-full w-fit h-fit px-2 py-1"/>
                                <div className="flex text-h2 flex-col gap-1">
                                    <div className="px-2 font-semibold">{dispUserName(user)}</div>
                                    <div className="px-2">{getUserIfExt(user)}</div>
                                </div>
                            </div>
                            {signOut && <>
                                <Divider/>
                                <Button className="px-2 w-full justify-center text-sm gap-1" onClick={signOut}>
                                    <i className="icon-exit"/> Logout
                                </Button>
                            </>}
                        </div>
                    }/>
                <Switch
                    onChange={_ => setTheme(curTheme !== "dark" ? "dark" : "light")}
                    value={curTheme}
                    field= {"theme"}
                    opts={themeMenuDefsOpts}/>
            </div>
            {panelsJsx}
        </div>
    );
}

function Notifications() {
    const {user} = useUser(), isViewOnly = checkRoleViewOnly(user?.perms);
    const [isDismissing, setIsDismissing] = useState(false);
    const {rsp: notifListRsp, refresh, isLoading: isFetching} = useFetchData({
        api: "/api/v2/notif_get-list", socket: socketNotifs, alt: mockData.notifGetList
    });
    const isLoading = isFetching || isDismissing;

    const onlyNewNotifications = useMemo(() => notifListRsp?.data?.filter(n => !n.dismissedBy), [notifListRsp?.data]);

    const handleDismissNotifications = (ids = []) => {
        queryOrErr({
            api: "/api/v2/notif_dismiss",
            alt: {},
            onLoading: setIsDismissing,
            params: {ids, socket: socketNotifs},
            rsp:() => refresh(),
        });
    };

    const tooltipText = `You have ${commafyPlural(onlyNewNotifications?.length, "new notification")} pending.`;

    const notificationButton =
        <Button data-test="notifications-btn" className="relative fill-title cursor-pointer"  {...genTooltip(tooltipText)}>
            {icn.bell}
            {onlyNewNotifications?.length > 0 && (
                <div className="absolute right-[-65%] top-[-55%] px-1 text-2xs text-center cursor-pointer animate-appear bg-sec/60 rounded-full text-prm-contrast">
                    {onlyNewNotifications?.length}
                </div>
            )}
        </Button>;

    const onNotificationPanel = ({closePanel}) =>
        <div data-test="notifications-panel" className="notifications-panel bg-bg1 py-4 text-title shadow-xl border border-bg5 rounded-lg whitespace-pre focus:outline-none flex flex-col gap-0 max-w-[33rem] min-h-[30vh]">
            <div className="flex justify-between px-4 border-b border-bg5 pb-4">
                <div className="flex flex-col items-start gap-1">
                    <Title className="text-lg">Notifications</Title>
                    <Title classNameColor="text-xs text-subtitle1">Purged after 14 days.</Title>
                </div>
                <div className="flex items-start justify-end gap-4">
                    {onlyNewNotifications?.length > 0 && (
                        <div className="bg-bg5 text-prm-light text-xs text-center py-1 px-2 rounded-xl shadow-sm cursor-default">
                            {onlyNewNotifications.length} New
                        </div>
                    )}
                </div>
            </div>

            <div className="flex flex-col gap-1 items-center mb-1">
                <div data-test="notifications-list" className={`flex flex-col w-full gap-0 min-w-[25rem] max-w-[25rem] max-h-[18rem] overflow-y-auto ${!notifListRsp?.data?.length ? "items-start" : ""} ${classNameScrollbarThin} scrollbar-thumb-badge hover:scrollbar-thumb-badge/75 scrollbar-track-transparent`}>
                    {notifListRsp?.data?.map(n => {
                        const source = n.source.substring(0, n.source.lastIndexOf(" - ")) || n.source;
                        const currUser = n.userName === user?.user;
                        const userType = !n.userName ? "system" : currUser ? "me" : "team";
                        const notifTitle = `${userType === "team" ? n.userName : ""} ${notifTitlesByUser[userType][n.type]?.h ?? n.type}`;
                        return <div key={n.id} className="group/row flex items-start gap-3 w-full cursor-default bg-bg1 hover:bg-bg5 border-b border-bg5 py-2 px-3">
                            <LetterAvatar className="h-8 self-start mt-1" user={n?.userName}/>
                            <div className="flex flex-col gap-1 flex-grow min-w-0">
                                <Title
                                    tip={notifTitle}
                                    classNameColor={`text-xs opacity-80 group-hover/row:opacity-100 transition-opacity ${!n.dismissedBy && "font-semibold"}`}>
                                    {notifTitle}
                                </Title>
                                <Title className="truncate" classNameColor="text-xs font-normal">{source}</Title>
                                {(n.hasPayload || n.type === "offlineExport") && <Button
                                    className="text-xs w-fit pr-1 flex-shrink-0 text-prm-light"
                                    h={<div><i className="icon-download"/> Download</div>}
                                    data-test="download-btn"
                                    disabled={!n.hasPayload && "In progress..."}
                                    onClick={n.type !== "clientLog" ? (
                                        e => {
                                            e.stopPropagation();
                                            queryOrErr({
                                                api: "/api/v2/notif_get-payload",
                                                params: {
                                                    ...pickKeys(n, "id", "type"),
                                                    socket: socketNotifs,
                                                },
                                                rsp: r => window.open(r.url, "_blank")      // opens a new tab instead of using downloadFile. couldn't overcome the sent content-type when using the 302 redirect method. @dim: seaweed builds the signature using several headers, Content-Type is one of them  if it's missing, it falls back to "" (and it is missing when we sign, so it uses "") if UI specifies Content-Type to anything except "" (which it does), signature is invalid.
                                            });
                                        }) : (
                                        e => {
                                            e.stopPropagation();
                                            downloadFile({
                                                api: "/api/v2/notif_get-payload",
                                                filename: nameExport(`${notifTypes[n.type]?.h} for ${n.source}`, ".zip"),
                                                params: {
                                                    ...pickKeys(n, "id", "type"),
                                                    socket: socketNotifs,
                                                }
                                            });
                                        })}/>}
                            </div>
                            <div className="flex flex-col gap-2 items-end">
                                <div className="flex items-center gap-2 mr-1">
                                    <Title classNameColor="text-2xs text-subtitle1">
                                        {getAgo(n.date)}
                                    </Title>
                                    {n.dismissedBy ? <div className="w-2 h-2"/> : <DotIndicator className="text-prm-dark w-2 h-2"/>}
                                </div>
                                {!isViewOnly && !n.dismissedBy &&
                                    <Button
                                        className="opacity-0 group-hover/row:opacity-100 transition-opacity icon-cross text-icon text-base cursor-pointer "
                                        title="Dismiss Notification" data-test="dismiss-btn" disabled={isLoading || n.dismissedBy}
                                        onClick={() => handleDismissNotifications([n.id])}/>}
                            </div>
                        </div>;
                    })}
                </div>

                {!notifListRsp?.data?.length
                    ? <div className="flex flex-col gap-2 items-center justify-center flex-1 min-h-[20vh]">
                        <div className="w-6 fill-gray-400">{icn.bell}</div>
                        <Title className="text-sm">No notifications available</Title>
                    </div>
                    : <Button
                        data-test="view-all-notifications-btn"
                        className="flex justify-center w-full max-w-[22rem] bg-prm-main text-white mt-4 px-4 py-1"
                        onClick={() => {
                            appCtx.nav.to(ROUTES.HEALTH, {}, {hash: "system-notifications", isReset: true});
                            closePanel();
                        }}>
                        View all notifications
                    </Button>
                }
            </div>
        </div>;

    return <PopupPanel
        button={notificationButton}
        onShowPanel={onNotificationPanel}
        align="bottom-right" margin={{x: 10, y: 15}}/>;
}

export function Breadcrumbs({className}) {
    const paths = appCtx.nav.getPaths();

    return (
        <div className={`${className} flex items-center gap-4 min-w-0`}>
            {paths.length > 1 &&
                <Button
                    onClick={() => appCtx.nav.navUp(paths.length - 2)}
                    data-test="back-button"
                    className="shrink-0 icon-back text-prm-light text-sm border border-prm-light rounded-full w-7 h-7 flex items-center justify-center"/>}
            <div className="flex flex-col overflow-hidden">
                <BreadcrumbsPath paths={paths}/>
                <Title data-test="page-title" children={appCtx.nav.getTitle()} className="text-[1.75rem]"/>
            </div>
        </div>
    );
}

const BreadcrumbsPath = ({paths}) => {
    const pathsList = paths?.slice(0, -1) ?? [];

    return (
        <div className="flex items-center flex-nowrap gap-1">
            {pathsList.map((_, i) => {
                const path = appCtx.nav.getTitle(i);

                return (
                    <div
                        key={i}
                        className="flex min-w-0 items-center gap-1 text-prm-light">
                        <Button
                            onClick={() => appCtx.nav.navUp(i)}
                            className="min-w-0 truncate !inline"
                            title={path}
                            h={path}/>
                        {i < pathsList.length - 1 && (
                            <span className="text-xs shrink-0">{">"}</span>
                        )}
                    </div>
                );
            })}
        </div>
    );
};
